import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  FileText, FolderClosed, Upload, CalendarDays, User, Clock, CheckCircle2,
  ChevronRight, Code2, Video, Link as LinkIcon, Presentation, StickyNote, X, Mic,
  type LucideIcon,
} from 'lucide-react';

import { dueDateTime, CATEGORY_META } from './practiceMeta';

import { usePractice, usePracticeAssessment } from '@/lib/hooks/useLms';
import { assessmentsApi } from '@/lib/api/assessments';
import { Html } from '@/features/ProblemWorkspace/ProblemDescription';
import { queryKeys } from '@/lib/queryKeys';
import type { Resource } from '@/lib/api/lms';
import type { AssessmentRubric } from '@/lib/api/rubric';
import { RichText, isRichTextEmpty } from '@/components/RichText';

const CARD = 'rounded-[20px] border border-[#ececec] bg-white shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]';
const GRADIENT = 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)';

const RES_META: Record<string, { icon: LucideIcon; bg: string; fg: string; sub: string }> = {
  pdf: { icon: FileText, bg: '#fee2e2', fg: '#ef4444', sub: 'PDF document' },
  slides: { icon: Presentation, bg: '#fff3ee', fg: '#ff5a1f', sub: 'Slides' },
  code_repo: { icon: Code2, bg: '#ede9fe', fg: '#8b5cf6', sub: 'Code repository' },
  recording: { icon: Video, bg: '#ede9fe', fg: '#8b5cf6', sub: 'Recording' },
  link: { icon: LinkIcon, bg: '#dcfce7', fg: '#16a34a', sub: 'External link' },
  notes: { icon: StickyNote, bg: '#fef3c7', fg: '#f59e0b', sub: 'Notes' },
};

/**
 * The marking scheme, shown to the student *before* they submit.
 *
 * A rubric the person being marked cannot read is not a rubric, it is a secret
 * — publishing it is the whole point. Once a grader has entered a breakdown,
 * each line also shows what was actually awarded, so a total can be explained
 * rather than merely asserted.
 */
function RubricPanel({
  rubric,
  maxScore,
  awarded,
}: {
  rubric: AssessmentRubric;
  maxScore?: number | null;
  awarded?: Record<string, number>;
}) {
  const graded = awarded != null && Object.keys(awarded).length > 0;
  const total = graded
    ? rubric.criteria.reduce((sum, c) => sum + (awarded[c.id] ?? 0), 0)
    : rubric.criteria.reduce((sum, c) => sum + c.max_marks, 0);

  return (
    <div className={`${CARD} p-6 sm:p-8`}>
      <h2 className="text-[20px] font-bold text-[#111827]">Marking Scheme</h2>
      <p className="mt-1 text-[13px] text-[#9ca3af]">
        {graded ? 'How your marks were awarded.' : 'How this will be marked. Read it before you start.'}
      </p>
      <ul className="mt-4 divide-y divide-[#f3f4f6] border-t border-[#f3f4f6]">
        {rubric.criteria.map((c) => (
          <li
            key={c.id}
            // Stacked below sm so the mark never crowds the label at 390px.
            className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6"
          >
            <div className="min-w-0">
              {/* Rubric criteria are authored with `inline code` around the
                  identifiers they are actually about -- `const`, `explicit`,
                  `virtual`. Rendered as plain text those backticks reach the
                  page. Same renderer as the statement and the editorial, so
                  code in a criterion looks like code everywhere. */}
              <div className="text-[14px] font-semibold text-[#111827]">
                <Html html={c.label} className="prose-p:my-0 [&_p]:inline" />
              </div>
              {c.descriptor && (
                <div className="mt-0.5 text-[13px] leading-relaxed text-[#6b7280]">
                  <Html html={c.descriptor} className="prose-p:my-0 [&_p]:inline" />
                </div>
              )}
            </div>
            <span className="shrink-0 text-[13px] font-semibold tabular-nums text-[#374151]">
              {graded ? `${awarded[c.id] ?? 0} / ${c.max_marks}` : `${c.max_marks} marks`}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between border-t border-[#f3f4f6] pt-3">
        <span className="text-[14px] font-bold text-[#111827]">Total</span>
        <span className="text-[14px] font-bold tabular-nums text-[#111827]">
          {total}
          {maxScore != null && <span className="font-normal text-[#9ca3af]"> / {maxScore}</span>}
        </span>
      </div>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 text-[13px]">
      <span className="text-[#9ca3af]">{label}</span>
      <span className="text-right font-medium text-[#374151]">{children}</span>
    </div>
  );
}

function ResourceRow({ resource }: { resource: Resource }) {
  const meta = RES_META[resource.type] ?? RES_META.link;
  const Icon = meta.icon;
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 px-1 py-2.5 transition-colors hover:opacity-80"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-[10px]" style={{ background: meta.bg }}>
        <Icon className="size-4" style={{ color: meta.fg }} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-[#111827]">{resource.title}</p>
        <p className="truncate text-[12px] text-[#9ca3af]">{meta.sub}</p>
      </div>
      <ChevronRight className="size-4 shrink-0 text-[#d1d5db]" />
    </a>
  );
}

export function AssignmentDetail({
  batchId,
  category,
  assessmentId,
}: {
  batchId: string;
  category: string;
  assessmentId: string;
}) {
  const { data, isLoading, isError, refetch } = usePracticeAssessment(assessmentId);
  const { data: practice } = usePractice(batchId);
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [url, setUrl] = useState('');

  const submit = useMutation({
    mutationFn: (submissionUrl: string) => assessmentsApi.submitAssignment(assessmentId, submissionUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.lms.practiceAssessment(assessmentId) });
      qc.invalidateQueries({ queryKey: queryKeys.lms.practice(batchId) });
      toast.success('Assignment submitted');
      setModalOpen(false);
    },
    onError: () => toast.error('Could not submit — check the link and try again.'),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#8b5cf6] border-t-transparent" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[1150px] p-4 sm:p-6">
        <div className={`${CARD} p-6 text-center`}>
          <p className="text-[#9ca3af]">Couldn&apos;t load this assignment.</p>
          <button onClick={() => refetch()} className="mt-3 rounded-[14px] bg-[#8b5cf6] px-4 py-2 text-sm font-semibold text-white">Retry</button>
        </div>
      </div>
    );
  }

  const rubric = data.rubric && data.rubric.criteria.length > 0 ? data.rubric : null;
  const isProject = data.category === 'projects';
  // A viva is marked in the room, so this page has nothing to upload and
  // nothing to link to. It shows what it is worth, what it will be marked
  // against, and — afterwards — the mark. The submit button is not merely
  // hidden: the server refuses a hand-in against a viva, and offering a
  // control that cannot succeed is worse than not offering one.
  const isViva = data.category === 'viva';
  const HeadIcon = isViva ? Mic : isProject ? FolderClosed : FileText;
  const due = dueDateTime(data.due_date);
  const categoryLabel = CATEGORY_META[data.category].label;
  const noun = isViva ? 'Viva' : isProject ? 'Project' : 'Assignment';

  return (
    <div className="mx-auto w-full max-w-[1150px] space-y-6 px-4 py-6 sm:px-10 sm:py-8">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] font-medium text-[#9ca3af]" aria-label="Breadcrumb">
        <Link to="/courses" className="transition-colors hover:text-[#ff5a1f]">My Batches</Link>
        <span aria-hidden>/</span>
        <Link to="/courses/$batchId" params={{ batchId }} className="text-[#ff5a1f] transition-colors hover:opacity-80">{practice?.batch_name ?? 'Batch'}</Link>
        <span aria-hidden>/</span>
        <Link to="/courses/$batchId/practice" params={{ batchId }} className="transition-colors hover:text-[#ff5a1f]">Practice</Link>
        <span aria-hidden>/</span>
        <Link to="/courses/$batchId/practice/$category" params={{ batchId, category }} className="transition-colors hover:text-[#ff5a1f]">{categoryLabel}</Link>
        <span aria-hidden>/</span>
        <span className="max-w-[220px] truncate font-semibold text-[#111827]">{data.title}</span>
      </nav>

      {/* Header card */}
      <header className={`${CARD} flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8`}>
        <div className="flex min-w-0 items-start gap-4">
          <span
            className="flex size-14 shrink-0 items-center justify-center rounded-[18px] sm:size-[72px]"
            style={{ background: 'linear-gradient(135deg, #ede9fe 0%, #fce7f3 100%)' }}
          >
            <HeadIcon className="size-7 text-[#8b5cf6] sm:size-9" />
          </span>
          <div className="min-w-0">
            <span className="inline-flex items-center rounded-full bg-[#ede9fe] px-2.5 py-1 text-[11px] font-semibold text-[#8b5cf6]">{data.module_title}</span>
            <h1 className="mt-1.5 text-[24px] font-bold leading-tight tracking-tight text-[#111827] sm:text-[30px]">{data.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#9ca3af]">
              {due && <span className="inline-flex items-center gap-1.5"><CalendarDays className="size-4" />Due {due}</span>}
              {data.created_by_name && (
                <span className="inline-flex items-center gap-1.5"><User className="size-4" />Created by {data.created_by_name}</span>
              )}
            </div>
          </div>
        </div>
        {isViva ? (
          <div className="flex shrink-0 items-center gap-2 rounded-[14px] bg-[#e0f2fe] px-4 py-3 text-[13px] font-semibold text-[#0369a1]">
            <Mic className="size-4 shrink-0" />
            <span>Assessed in person by your trainer</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setUrl(data.submission_url ?? ''); setModalOpen(true); }}
            className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-[14px] px-6 text-sm font-semibold text-white shadow-[0px_8px_18px_rgba(139,92,246,0.25)] transition-opacity hover:opacity-90"
            style={{ background: GRADIENT }}
          >
            <Upload className="size-4" />
            {data.submitted ? 'Update Submission' : `Submit ${noun}`}
          </button>
        )}
      </header>

      {/* Two-column body */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        {/* Left: Brief, then the marking scheme it will be judged by */}
        <div className="space-y-6">
          <div className={`${CARD} p-6 sm:p-8`}>
            <h2 className="text-[20px] font-bold text-[#111827]">{isViva ? 'What To Prepare' : `${noun} Brief`}</h2>
            <div className="mt-5 border-t border-[#f3f4f6] pt-5">
              {isRichTextEmpty(data.description) ? (
                <p className="text-[14px] text-[#9ca3af]">No brief has been added yet. Check back later or reach out to your trainer.</p>
              ) : (
                <RichText html={data.description!} className="text-[14px] leading-relaxed text-[#374151]" />
              )}
            </div>
          </div>
          {rubric && (
            <RubricPanel rubric={rubric} maxScore={data.max_score} awarded={data.rubric_scores} />
          )}
        </div>

        {/* Right: sidebar */}
        <aside className="space-y-6">
          {/* Info */}
          <div className={`${CARD} p-5`}>
            <h3 className="text-[15px] font-bold text-[#111827]">{noun} Info</h3>
            <div className="mt-2 divide-y divide-[#f3f4f6]">
              {due && <InfoRow label={isViva ? 'Scheduled For' : 'Due Date'}>{due}</InfoRow>}
              <InfoRow label="Module">{data.module_title}</InfoRow>
              {data.max_score != null && <InfoRow label="Marks">{data.max_score} Marks</InfoRow>}
              <InfoRow label="Type">{isViva ? 'Oral, one-to-one' : 'Individual'}</InfoRow>
              {!isViva && <InfoRow label="Attempts Allowed">1</InfoRow>}
              <InfoRow label="Status">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${data.submitted ? 'bg-[#dcfce7] text-[#16a34a]' : 'bg-[#ede9fe] text-[#8b5cf6]'}`}>
                  {isViva
                    ? (data.score != null ? 'Assessed' : 'Not Assessed Yet')
                    : (data.submitted ? 'Submitted' : 'Not Submitted')}
                </span>
              </InfoRow>
            </div>
          </div>

          {/* Submission status — or, for a viva, the mark, since there is no
              submission whose status could be reported. */}
          {isViva ? (
            <div className={`${CARD} p-5`}>
              <h3 className="text-[15px] font-bold text-[#111827]">Your Result</h3>
              <div className="mt-3 flex items-start gap-3">
                <span className={`flex size-9 shrink-0 items-center justify-center rounded-full ${data.score != null ? 'bg-[#dcfce7]' : 'bg-[#e0f2fe]'}`}>
                  {data.score != null ? <CheckCircle2 className="size-5 text-[#16a34a]" /> : <Mic className="size-5 text-[#0284c7]" />}
                </span>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-[#111827]">
                    {data.score != null
                      ? `${data.score}${data.max_score != null ? `/${data.max_score}` : ''}`
                      : 'Not assessed yet'}
                  </p>
                  <p className="text-[13px] text-[#9ca3af]">
                    {data.score != null
                      ? 'Marked by your trainer during the viva.'
                      : 'Your trainer will assess you in person and record your mark here.'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
          <div className={`${CARD} p-5`}>
            <h3 className="text-[15px] font-bold text-[#111827]">Submission Status</h3>
            <div className="mt-3 flex items-start gap-3">
              <span className={`flex size-9 shrink-0 items-center justify-center rounded-full ${data.submitted ? 'bg-[#dcfce7]' : 'bg-[#ede9fe]'}`}>
                {data.submitted ? <CheckCircle2 className="size-5 text-[#16a34a]" /> : <Clock className="size-5 text-[#8b5cf6]" />}
              </span>
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-[#111827]">{data.submitted ? 'Submitted' : 'Not Submitted'}</p>
                {data.submitted ? (
                  <p className="text-[13px] text-[#9ca3af]">
                    {data.score != null ? `Graded — ${data.score}${data.max_score != null ? `/${data.max_score}` : ''}` : 'Awaiting grading.'}
                    {data.submission_url && (
                      <> · <a href={data.submission_url} target="_blank" rel="noreferrer" className="font-medium text-[#8b5cf6] hover:underline">View submission</a></>
                    )}
                  </p>
                ) : (
                  <p className="text-[13px] text-[#9ca3af]">Submit before the due date to avoid penalty.</p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Resources */}
          {data.resources.length > 0 && (
            <div className={`${CARD} p-5`}>
              <h3 className="text-[15px] font-bold text-[#111827]">Resources</h3>
              <div className="mt-2 divide-y divide-[#f3f4f6]">
                {data.resources.map((r) => <ResourceRow key={r.id} resource={r} />)}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Submit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-md rounded-[20px] bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-[18px] font-bold text-[#111827]">Submit {noun}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="text-[#9ca3af] hover:text-[#374151]"><X className="size-5" /></button>
            </div>
            <p className="mt-1 text-[13px] text-[#6b7280]">Paste a link to your work (GitHub repo, CodeSandbox, Google Drive, etc.).</p>
            <input
              type="url"
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://github.com/you/your-project"
              className="mt-4 w-full rounded-[12px] border border-[#e5e7eb] px-3.5 py-2.5 text-sm focus:border-[#8b5cf6] focus:outline-none focus:ring-2 focus:ring-[#ede9fe]"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-[12px] border border-[#e5e7eb] px-4 py-2 text-sm font-semibold text-[#374151] hover:bg-[#f9fafb]">Cancel</button>
              <button
                type="button"
                disabled={submit.isPending || !url.trim()}
                onClick={() => submit.mutate(url.trim())}
                className="inline-flex items-center justify-center rounded-[12px] px-5 py-2 text-sm font-semibold text-white shadow-[0px_8px_18px_rgba(139,92,246,0.25)] transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: GRADIENT }}
              >
                {submit.isPending ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
